/**
 * Reportes Hacienda - Finance 360
 * Reports for Costa Rica tax authorities: IVA (D-150, antes D-104), Retenciones (D-103), Renta (D-101)
 * 26/10/2026: antes decía «D-270 (IVA)» y «D-104 (Retenciones)». La D-270 es la informativa de clientes
 * y proveedores sin comprobante; el IVA era la D-104 (D-150 en TRIBU-CR desde oct-2025) y las retenciones la D-103.
 * Los id internos (d270, d104) y las rutas del API no se cambian.
 */

import { useState, useEffect } from 'react';
import {
  FileText,
  Download,
  RefreshCw,
  Calculator,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Building2,
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Printer
} from 'lucide-react';
import { contabilidadApi } from '../../utils/api';
import useAuthStore from '../../store/authStore';
import { LoadingPage } from '../../components/LoadingSpinner';
import toast from 'react-hot-toast';

// Toolbar button
function ToolbarButton({ icon: Icon, label, onClick, disabled, variant = 'default' }) {
  const baseClasses = "flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px]";
  const variants = {
    default: "border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400",
    primary: "border border-blue-400 text-blue-600 hover:bg-blue-50 hover:border-blue-500",
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variants[variant]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <Icon className="h-5 w-5 mb-1" />
      <span className="whitespace-nowrap">{label}</span>
    </button>
  );
}

// Report type tabs
const REPORT_TYPES = [
  { id: 'd270', name: 'IVA (D-150)', icon: Calculator, description: 'Declaración de IVA (antes D-104)' },
  { id: 'd104', name: 'Retenciones (D-103)', icon: DollarSign, description: 'Retenciones en la fuente' },
  { id: 'd101', name: 'Renta (D-101)', icon: Building2, description: 'Impuesto sobre la renta' }
];

export default function ReportesHacienda() {
  const { currentEmisor } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeReport, setActiveReport] = useState('d270');
  const [periodos, setPeriodos] = useState([]);
  const [selectedPeriodo, setSelectedPeriodo] = useState('');
  const [reportData, setReportData] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    ventas: true,
    compras: true,
    resumen: true
  });

  useEffect(() => {
    if (currentEmisor) {
      loadPeriodos();
    }
  }, [currentEmisor, activeReport]);

  const loadPeriodos = async () => {
    setLoading(true);
    try {
      const tipo = activeReport === 'd101' ? 'anual' : 'mensual';
      const response = await contabilidadApi.getHaciendaPeriodos(tipo);
      setPeriodos(response.data.data || []);
      if (response.data.data?.length > 0) {
        setSelectedPeriodo(response.data.data[0].valor);
      }
    } catch (error) {
      console.error('Error loading periodos:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async () => {
    if (!selectedPeriodo) {
      toast.error('Seleccione un período');
      return;
    }

    setGenerating(true);
    try {
      let response;
      switch (activeReport) {
        case 'd270':
          response = await contabilidadApi.getD270(selectedPeriodo);
          break;
        case 'd104':
          response = await contabilidadApi.getD104(selectedPeriodo);
          break;
        case 'd101':
          response = await contabilidadApi.getD101(selectedPeriodo);
          break;
      }
      setReportData(response.data.data);
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error(error.response?.data?.error || 'Error al generar reporte');
    } finally {
      setGenerating(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: 'CRC',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return <LoadingPage />;
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="bg-white rounded-lg border p-3 relative min-h-[70px]">
        {/* Period selector - Left */}
        <div className="flex items-center gap-3">
          <select
            value={selectedPeriodo}
            onChange={(e) => setSelectedPeriodo(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 min-w-[200px]"
          >
            <option value="">Seleccione período...</option>
            {periodos.map((p) => (
              <option key={p.valor} value={p.valor}>{p.descripcion}</option>
            ))}
          </select>
        </div>

        {/* Toolbar buttons - Centered */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-2">
          <ToolbarButton
            icon={FileText}
            label="Generar Reporte"
            onClick={generateReport}
            disabled={generating || !selectedPeriodo}
            variant="primary"
          />
          <ToolbarButton
            icon={Printer}
            label="Imprimir"
            onClick={handlePrint}
            disabled={!reportData}
          />
          <ToolbarButton
            icon={RefreshCw}
            label="Actualizar"
            onClick={loadPeriodos}
            disabled={loading}
          />
        </div>
      </div>

      {/* Report Type Tabs */}
      <div className="bg-white rounded-lg border p-1 flex gap-1">
        {REPORT_TYPES.map((type) => (
          <button
            key={type.id}
            onClick={() => {
              setActiveReport(type.id);
              setReportData(null);
            }}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-all ${
              activeReport === type.id
                ? 'bg-blue-50 text-blue-700 border border-blue-200'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <type.icon className="h-5 w-5" />
            <div className="text-left">
              <p className="font-medium">{type.name}</p>
              <p className="text-xs opacity-70">{type.description}</p>
            </div>
          </button>
        ))}
      </div>

      {/* Report Content */}
      {!reportData ? (
        <div className="bg-white rounded-xl border p-12 text-center">
          <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {REPORT_TYPES.find(t => t.id === activeReport)?.name}
          </h3>
          <p className="text-gray-500 mb-6">
            Seleccione un período y genere el reporte para ver los datos
          </p>
          <button
            onClick={generateReport}
            disabled={!selectedPeriodo}
            className="px-6 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-50"
          >
            Generar Reporte
          </button>
        </div>
      ) : (
        <div className="space-y-4 print:space-y-2">
          {/* Report Header */}
          <div className="bg-white rounded-xl border p-6 print:border-0">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {REPORT_TYPES.find(t => t.id === activeReport)?.name}
                </h2>
                <p className="text-gray-500">{reportData.periodo?.descripcion}</p>
              </div>
              <div className="text-right text-sm text-gray-500">
                <p>Desde: {reportData.periodo?.fechaInicio}</p>
                <p>Hasta: {reportData.periodo?.fechaFin}</p>
              </div>
            </div>
          </div>

          {/* D-270 Content */}
          {activeReport === 'd270' && <D270Report data={reportData} formatCurrency={formatCurrency}
            expandedSections={expandedSections} toggleSection={toggleSection} />}

          {/* D-104 Content */}
          {activeReport === 'd104' && <D104Report data={reportData} formatCurrency={formatCurrency} />}

          {/* D-101 Content */}
          {activeReport === 'd101' && <D101Report data={reportData} formatCurrency={formatCurrency} />}
        </div>
      )}
    </div>
  );
}

// D-270 IVA Report Component - BASADO EN CONTABILIDAD
function D270Report({ data, formatCurrency, expandedSections, toggleSection }) {
  return (
    <>
      {/* Alerta si no hay datos contables */}
      {data.alerta && (
        <div className={`rounded-xl border p-4 flex items-start gap-3 ${
          data.alerta.tipo === 'warning' ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'
        }`}>
          <AlertCircle className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
            data.alerta.tipo === 'warning' ? 'text-amber-600' : 'text-blue-600'
          }`} />
          <div>
            <p className={`font-medium ${data.alerta.tipo === 'warning' ? 'text-amber-800' : 'text-blue-800'}`}>
              {data.alerta.tipo === 'warning' ? 'Sin datos contables' : 'Documentos pendientes'}
            </p>
            <p className={`text-sm ${data.alerta.tipo === 'warning' ? 'text-amber-700' : 'text-blue-700'}`}>
              {data.alerta.mensaje}
            </p>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 bg-red-50 rounded-lg flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">IVA Débito Fiscal</p>
              <p className="text-xl font-bold text-red-600">{formatCurrency(data.resumen?.ivaDebito)}</p>
            </div>
          </div>
          <p className="text-xs text-gray-500">Cuenta 2.1.02 - IVA por pagar</p>
        </div>

        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 bg-green-50 rounded-lg flex items-center justify-center">
              <TrendingDown className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">IVA Crédito Fiscal</p>
              <p className="text-xl font-bold text-green-600">{formatCurrency(data.resumen?.ivaCredito)}</p>
            </div>
          </div>
          <p className="text-xs text-gray-500">Cuenta 1.1.04 - IVA a favor</p>
        </div>

        <div className={`rounded-xl border p-5 ${
          data.resumen?.ivaAPagar > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
        }`}>
          <div className="flex items-center gap-3 mb-3">
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
              data.resumen?.ivaAPagar > 0 ? 'bg-red-100' : 'bg-green-100'
            }`}>
              <DollarSign className={`h-5 w-5 ${data.resumen?.ivaAPagar > 0 ? 'text-red-600' : 'text-green-600'}`} />
            </div>
            <div>
              <p className="text-xs text-gray-500">
                {data.resumen?.ivaAPagar > 0 ? 'IVA a Pagar' : 'Crédito a Favor'}
              </p>
              <p className={`text-xl font-bold ${data.resumen?.ivaAPagar > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {formatCurrency(Math.abs(data.resumen?.ivaAPagar))}
              </p>
            </div>
          </div>
          <p className="text-xs text-gray-500">
            {data.resumen?.ivaAPagar > 0 ? 'Débito - Crédito' : 'Crédito > Débito'}
          </p>
        </div>
      </div>

      {/* Detalle IVA Débito (Cuentas 2.1.02.xxx) */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <button
          onClick={() => toggleSection('ventas')}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
        >
          <div className="flex items-center gap-2">
            {expandedSections.ventas ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
            <h3 className="font-semibold text-gray-900">Detalle IVA Débito Fiscal</h3>
          </div>
          <span className="text-lg font-bold text-red-600">{formatCurrency(data.resumen?.ivaDebito)}</span>
        </button>

        {expandedSections.ventas && (
          <div className="p-4 pt-0">
            {data.ivaDebitoDetalle?.length > 0 ? (
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Código</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Cuenta</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Saldo</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.ivaDebitoDetalle.map((c, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2 font-mono text-xs">{c.codigo}</td>
                      <td className="px-3 py-2">{c.nombre}</td>
                      <td className="px-3 py-2 text-right text-red-600 font-medium">{formatCurrency(c.saldo)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-red-50">
                  <tr>
                    <td colSpan="2" className="px-3 py-2 font-semibold">Total IVA Débito</td>
                    <td className="px-3 py-2 text-right text-red-600 font-bold">{formatCurrency(data.resumen?.ivaDebito)}</td>
                  </tr>
                </tfoot>
              </table>
            ) : (
              <div className="p-4 text-center text-gray-500">
                <p className="text-sm">No hay movimientos en cuentas 2.1.02.xxx</p>
                <p className="text-xs mt-1">Genere asientos contables para ver datos aquí</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Detalle IVA Crédito (Cuentas 1.1.04.xxx) */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <button
          onClick={() => toggleSection('compras')}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
        >
          <div className="flex items-center gap-2">
            {expandedSections.compras ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
            <h3 className="font-semibold text-gray-900">Detalle IVA Crédito Fiscal</h3>
          </div>
          <span className="text-lg font-bold text-green-600">{formatCurrency(data.resumen?.ivaCredito)}</span>
        </button>

        {expandedSections.compras && (
          <div className="p-4 pt-0">
            {data.ivaCreditoDetalle?.length > 0 ? (
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Código</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Cuenta</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Saldo</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.ivaCreditoDetalle.map((c, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2 font-mono text-xs">{c.codigo}</td>
                      <td className="px-3 py-2">{c.nombre}</td>
                      <td className="px-3 py-2 text-right text-green-600 font-medium">{formatCurrency(c.saldo)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-green-50">
                  <tr>
                    <td colSpan="2" className="px-3 py-2 font-semibold">Total IVA Crédito</td>
                    <td className="px-3 py-2 text-right text-green-600 font-bold">{formatCurrency(data.resumen?.ivaCredito)}</td>
                  </tr>
                </tfoot>
              </table>
            ) : (
              <div className="p-4 text-center text-gray-500">
                <p className="text-sm">No hay movimientos en cuentas 1.1.04.xxx</p>
                <p className="text-xs mt-1">Genere asientos contables para ver datos aquí</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Estadísticas de contabilización */}
      {data.estadisticas && (
        <div className="bg-white rounded-xl border p-4">
          <h3 className="font-semibold text-gray-900 mb-3">Estado de Contabilización</h3>
          <div className="grid grid-cols-4 gap-3">
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-blue-600">{data.estadisticas.asientosContabilizados}</p>
              <p className="text-xs text-gray-500">Asientos contabilizados</p>
            </div>
            <div className={`p-3 rounded-lg text-center ${data.estadisticas.documentosSinContabilizar > 0 ? 'bg-amber-50' : 'bg-green-50'}`}>
              <p className={`text-2xl font-bold ${data.estadisticas.documentosSinContabilizar > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                {data.estadisticas.documentosSinContabilizar}
              </p>
              <p className="text-xs text-gray-500">Ventas sin contabilizar</p>
            </div>
            <div className={`p-3 rounded-lg text-center ${data.estadisticas.comprasSinContabilizar > 0 ? 'bg-amber-50' : 'bg-green-50'}`}>
              <p className={`text-2xl font-bold ${data.estadisticas.comprasSinContabilizar > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                {data.estadisticas.comprasSinContabilizar}
              </p>
              <p className="text-xs text-gray-500">Compras sin contabilizar</p>
            </div>
            <div className={`p-3 rounded-lg text-center ${(data.estadisticas.fecSinContabilizar || 0) > 0 ? 'bg-amber-50' : 'bg-green-50'}`}>
              <p className={`text-2xl font-bold ${(data.estadisticas.fecSinContabilizar || 0) > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                {data.estadisticas.fecSinContabilizar || 0}
              </p>
              <p className="text-xs text-gray-500">FEC sin contabilizar</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// D-104 Withholding Report Component
function D104Report({ data, formatCurrency }) {
  return (
    <>
      {/* Notice about retenciones */}
      {data.resumen?.mensaje && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-yellow-800">Módulo en desarrollo</p>
            <p className="text-sm text-yellow-700">{data.resumen.mensaje}</p>
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border p-5">
          <p className="text-xs text-gray-500">Total Documentos</p>
          <p className="text-2xl font-bold text-gray-900">{data.resumen?.totalDocumentos}</p>
        </div>
        <div className="bg-white rounded-xl border p-5">
          <p className="text-xs text-gray-500">Total Proveedores</p>
          <p className="text-2xl font-bold text-blue-600">{data.resumen?.totalProveedores}</p>
        </div>
        <div className="bg-white rounded-xl border p-5">
          <p className="text-xs text-gray-500">Total Compras</p>
          <p className="text-2xl font-bold text-green-600">{formatCurrency(data.resumen?.totalCompras)}</p>
        </div>
      </div>

      {/* Por Proveedor */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="p-4 border-b">
          <h3 className="font-semibold text-gray-900">Compras por Proveedor</h3>
        </div>
        {data.retenciones?.porProveedor?.length > 0 ? (
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Proveedor</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Cédula</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">Docs</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">Compras</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.retenciones.porProveedor.map((p, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{p.nombre}</td>
                  <td className="px-4 py-3 text-gray-500">{p.cedula}</td>
                  <td className="px-4 py-3 text-right">{p.documentos}</td>
                  <td className="px-4 py-3 text-right">{formatCurrency(p.compras)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50 font-medium">
              <tr>
                <td colSpan="3" className="px-4 py-3 text-right">Total:</td>
                <td className="px-4 py-3 text-right text-green-600">{formatCurrency(data.resumen?.totalCompras)}</td>
              </tr>
            </tfoot>
          </table>
        ) : (
          <div className="p-8 text-center text-gray-500">
            <CheckCircle2 className="h-12 w-12 text-green-300 mx-auto mb-3" />
            <p>No hay compras en este período</p>
          </div>
        )}
      </div>
    </>
  );
}

// D-101 Income Tax Report Component
function D101Report({ data, formatCurrency }) {
  return (
    <>
      {/* Results Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border p-5">
          <p className="text-xs text-gray-500">Ingresos Brutos</p>
          <p className="text-xl font-bold text-gray-900">{formatCurrency(data.resultados?.ingresosBrutos)}</p>
        </div>
        <div className="bg-white rounded-xl border p-5">
          <p className="text-xs text-gray-500">Utilidad Bruta</p>
          <p className="text-xl font-bold text-blue-600">{formatCurrency(data.resultados?.utilidadBruta)}</p>
        </div>
        <div className="bg-white rounded-xl border p-5">
          <p className="text-xs text-gray-500">Utilidad Operativa</p>
          <p className="text-xl font-bold text-green-600">{formatCurrency(data.resultados?.utilidadOperativa)}</p>
        </div>
        <div className={`rounded-xl border p-5 ${data.resultados?.impuestoRenta > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50'}`}>
          <p className="text-xs text-gray-500">Impuesto sobre Renta</p>
          <p className="text-xl font-bold text-red-600">{formatCurrency(data.resultados?.impuestoRenta)}</p>
        </div>
      </div>

      {/* Income Detail */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Ingresos</h3>
          <span className="text-lg font-bold text-green-600">{formatCurrency(data.ingresos?.total)}</span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Código</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Cuenta</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Monto</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.ingresos?.detalle?.map((item, i) => (
              <tr key={i} className="hover:bg-gray-50">
                <td className="px-4 py-2 font-mono text-xs">{item.codigo}</td>
                <td className="px-4 py-2">{item.nombre}</td>
                <td className="px-4 py-2 text-right text-green-600">{formatCurrency(item.monto)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Costs Detail */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Costos de Ventas</h3>
          <span className="text-lg font-bold text-red-600">{formatCurrency(data.costos?.total)}</span>
        </div>
        {data.costos?.detalle?.length > 0 ? (
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Código</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Cuenta</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Monto</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.costos.detalle.map((item, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-mono text-xs">{item.codigo}</td>
                  <td className="px-4 py-2">{item.nombre}</td>
                  <td className="px-4 py-2 text-right text-red-600">{formatCurrency(item.monto)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-4 text-center text-gray-500 text-sm">Sin costos registrados</div>
        )}
      </div>

      {/* Expenses Detail */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Gastos Operativos</h3>
          <span className="text-lg font-bold text-orange-600">{formatCurrency(data.gastos?.total)}</span>
        </div>
        {data.gastos?.detalle?.length > 0 ? (
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Código</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Cuenta</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Monto</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.gastos.detalle.map((item, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-mono text-xs">{item.codigo}</td>
                  <td className="px-4 py-2">{item.nombre}</td>
                  <td className="px-4 py-2 text-right text-orange-600">{formatCurrency(item.monto)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-4 text-center text-gray-500 text-sm">Sin gastos registrados</div>
        )}
      </div>

      {/* Balance Summary */}
      <div className="bg-white rounded-xl border p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Posición Financiera al cierre</h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <p className="text-xs text-gray-500">Total Activos</p>
            <p className="text-xl font-bold text-blue-600">{formatCurrency(data.balance?.activos)}</p>
          </div>
          <div className="text-center p-4 bg-red-50 rounded-lg">
            <p className="text-xs text-gray-500">Total Pasivos</p>
            <p className="text-xl font-bold text-red-600">{formatCurrency(data.balance?.pasivos)}</p>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <p className="text-xs text-gray-500">Patrimonio</p>
            <p className="text-xl font-bold text-green-600">{formatCurrency(data.balance?.patrimonio)}</p>
          </div>
        </div>
      </div>

      {/* Final Result */}
      <div className={`rounded-xl border p-6 text-center ${
        data.resultados?.utilidadNeta >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
      }`}>
        <p className="text-sm text-gray-500 mb-1">Resultado Neto del Ejercicio</p>
        <p className={`text-3xl font-bold ${data.resultados?.utilidadNeta >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {formatCurrency(data.resultados?.utilidadNeta)}
        </p>
        <p className="text-sm text-gray-500 mt-2">
          {data.resultados?.utilidadNeta >= 0 ? 'Utilidad' : 'Pérdida'} después de impuestos
        </p>
      </div>
    </>
  );
}
